import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Thin wrappers over `@vibe/core` (spec M0-F7 §6.3).
 *
 * Each one exists for a reason the table in §6.3 states: a size lock measured from the
 * reference, a narrower prop shape, a mandatory accessibility prop, or a default that keeps
 * every instance in the product consistent. Every wrapper forwards `data-testid`, its aria
 * attributes and `ref` (C2) — the e2e suite selects on `data-testid`, never on a class name,
 * because monday's are hashed per deploy and so are Vibe's.
 */
import { Avatar as VibeAvatar, Button as VibeButton, DatePicker as VibeDatePicker, Dialog as VibeDialog, DialogContentContainer as VibeDialogContentContainer, Dropdown as VibeDropdown, IconButton as VibeIconButton, List as VibeList, ListItem as VibeListItem, MenuDivider, MenuTitle, Modal as VibeModal, ModalContent, ModalFooter, ModalHeader, Search as VibeSearch, Tab, TabList, TabPanel, TabPanels, TabsContext, TextField as VibeTextField, Toast as VibeToast, } from '@vibe/core';
import { forwardRef, useEffect, useRef, useState, } from 'react';
/**
 * Every observed board-header / toolbar button is 32 px high with 4–8 px padding
 * (`[uij:library/board-01-header.json#integrateButton]` → `h: 32`, `padding: 4px 8px`), so the
 * size is locked to `small` unless a caller opts out explicitly.
 */
export const Button = forwardRef(function Button({ kind = 'primary', size = 'small', ...props }, ref) {
    return _jsx(VibeButton, { ref: ref, kind: kind, size: size, ...props });
});
export const IconButton = forwardRef(function IconButton({ size = 'small', ariaLabel, tooltipContent, ...props }, ref) {
    return (_jsx(VibeIconButton, { ref: ref, size: size, "aria-label": ariaLabel, tooltipContent: tooltipContent ?? ariaLabel, ...props }));
});
export const TextField = forwardRef(function TextField({ size = 'small', ...props }, ref) {
    return _jsx(VibeTextField, { ref: ref, size: size, ...props });
});
export function Search({ size = 'small', onChange, debounceMs = 250, value, ...props }) {
    const [local, setLocal] = useState(value ?? '');
    const timer = useRef(null);
    useEffect(() => {
        if (value !== undefined)
            setLocal(value);
    }, [value]);
    useEffect(() => () => (timer.current ? clearTimeout(timer.current) : undefined), []);
    return (_jsx(VibeSearch, { size: size, value: local, onChange: (next) => {
            setLocal(next);
            if (!onChange)
                return;
            if (timer.current)
                clearTimeout(timer.current);
            timer.current = setTimeout(() => onChange(next), debounceMs);
        }, ...props }));
}
/**
 * Vibe 4.5 already renders the dropdown menu in a popup layer, so it escapes the sidebar's
 * `overflow: auto` without a `menuPortalTarget` (the prop the spec assumed does not exist in
 * this version). What the wrapper adds is our `{ id, label, icon }` option shape: screens never
 * build Vibe's `{ value, label }` pairs, so a change of dropdown library is one file.
 */
export function Dropdown({ options, value, onChange, size = 'small', searchable, vibeProps, ...props }) {
    const vibeOptions = options.map((option) => ({
        value: option.id,
        label: option.label,
        disabled: option.disabled,
    }));
    const selected = vibeOptions.find((option) => option.value === value);
    const merged = {
        size,
        options: vibeOptions,
        value: selected,
        onChange: (option) => onChange?.(option ? String(option.value) : null),
        ...(searchable ? { searchable: true } : {}),
        ...props,
        ...vibeProps,
    };
    return _jsx(VibeDropdown, { ...merged });
}
/* -------------------------------------------------------------------- Menu */
export { Menu, MenuButton, MenuDivider, MenuItem, MenuTitle } from '@vibe/core';
/**
 * Vibe has `MenuTitle` and `MenuDivider` but no section primitive, and every menu in the
 * reference (`[ui:library/board-03-three-dots-menu.png]`) is title + items + divider.
 */
export function MenuSection({ title, divider = true, children }) {
    return (_jsxs(_Fragment, { children: [title ? _jsx(MenuTitle, { caption: title, captionPosition: "top" }) : null, children, divider ? _jsx(MenuDivider, {}) : null] }));
}
/**
 * Dialogs and popovers render `radius 8` + `0 6px 20px rgba(0,0,0,.2)` in the reference
 * (`[uij:shell-01#openDialog]`), which is Vibe's `--box-shadow-medium`.
 */
export function Dialog({ showTrigger, hideTrigger, ...props }) {
    return (_jsx(VibeDialog, { showTrigger: showTrigger ?? ['click'], hideTrigger: hideTrigger ?? ['clickoutside', 'esckey'], ...props }));
}
export const DialogContentContainer = VibeDialogContentContainer;
/**
 * Vibe's `Modal` already owns the `--backdrop-color` backdrop, Escape-to-close and the focus
 * trap. What it does not own is the measured width: the invite modal is 620 px
 * (`[ui:library/board-04-share-dialog.png]` x 410→1030) and Vibe's sizes are
 * small / medium / large, so `.ez-modal` pins `--modal-width` in `components.css`.
 */
export function Modal({ className, children, ...props }) {
    return (_jsx(VibeModal, { className: ['ez-modal', className].filter(Boolean).join(' '), ...props, children: children }));
}
export { ModalContent, ModalFooter, ModalHeader };
/* ------------------------------------------------------------------- Toast */
export const Toast = VibeToast;
export function Avatar({ size = 'small', deactivated, ...props }) {
    return (_jsx(VibeAvatar, { size: size, ...props, className: [props.className, deactivated ? 'ez-avatar--deactivated' : null].filter(Boolean).join(' ') }));
}
/**
 * The tab bar. 42 px high with a 2 px `--primary-color` underline on the selected tab
 * (`[uij:library/board-01-header.json#viewTabsBar]` h=42) — Vibe's `TabList` with our height
 * locked on top of it. `Tabs` is the bar; `TabsContext` still wraps bar + panels when a screen
 * needs both.
 */
export function Tabs({ className, ...props }) {
    return _jsx(TabList, { className: ['ez-tabs', className].filter(Boolean).join(' '), ...props });
}
export { Tab, TabList, TabPanel, TabPanels, TabsContext };
export function DatePicker(props) {
    const { weekendDays, showWeekends, ...rest } = props;
    void weekendDays;
    void showWeekends;
    return _jsx(VibeDatePicker, { ...rest });
}
/* -------------------------------------------------------------------- List */
export const List = VibeList;
export const ListItem = VibeListItem;
//# sourceMappingURL=wrappers.js.map